import { ChangeDetectionStrategy, Component, Inject, Input, LOCALE_ID, OnChanges } from "@angular/core";
import { DecimalPipe, formatNumber } from "@angular/common";

import { esHttps, fechaDmy, num } from "../homologacion-formato";
import { HomologacionFicha, HomologacionFichaBenchmark } from "../../shared/model/iso42001/homologacion-modelo.model";

/** Con más días que estos desde «verificado», la ficha avisa (spec 5.8). No bloquea. */
const DIAS_AVISO = 90;

/** Los precios se guardan con hasta 6 decimales: se muestran todos, sin redondear. */
const FORMATO_PRECIO = "1.2-6";

/** Un precio de la ficha, por millón de tokens. */
interface Precio {
  concepto: string;
  valor: number;
}

/**
 * Los precios con que midió el harness la evaluación que está a la vista
 * (USD por millón de tokens, de columnas). Contra ellos se compara la ficha.
 */
export interface PreciosEvaluacion {
  idEval: number;
  entrada?: number | null;
  salida?: number | null;
  cacheLectura?: number | null;
}

/** Una lista de la ficha (disponibilidad, modalidades, fortalezas…), solo si trae renglones. */
interface Lista {
  titulo: string;
  items: string[];
}

/** Para que dos bloques en la misma pantalla no repitan el id del título. */
let siguiente = 0;

/**
 * «Ficha del catálogo» (spec 5.8): lo que publica el fabricante y opina el
 * Catálogo de Modelos de IA sobre el modelo. INFORMA; NO CALIFICA: no toca
 * veredicto, gates, capacidad, costo ni dictamen, y COS no recalcula nada
 * con ella. Sale de columnas y tablas hijas: el servidor nunca lee el
 * archivo para mostrarla.
 *
 * La usan el Resultado de la gestión y el Resultado resumido de la Consulta.
 *
 * El texto lo escribió un tercero: va SOLO por interpolación, que escapa.
 * Aquí no se inyecta HTML (ni binding de HTML crudo ni rodeos del
 * sanitizador) y no debe hacerse nunca. Una liga (la página del modelo en
 * el catálogo, la fuente, el índice o un benchmark) solo si empieza con
 * https:// (esHttps); si no, se pinta como texto. El logo del catálogo no
 * viaja ni se descarga.
 */
@Component({
  selector: "app-ficha-catalogo",
  standalone: true,
  imports: [DecimalPipe],
  templateUrl: "./ficha-catalogo.component.html",
  // Primero lo común (tarjetas, tablas internas, avisos), luego lo suyo.
  styleUrls: ["../homologacion-comun.css", "./ficha-catalogo.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FichaCatalogoComponent implements OnChanges {
  @Input({ required: true }) ficha!: HomologacionFicha;
  /**
   * Los precios de la evaluación que se está viendo (el Resultado de la
   * gestión o el resumido de la Consulta). Sin ellos se compara contra los
   * de la última evaluación no reemplazada del modelo, que manda el servidor
   * en la ficha, y el aviso lo dice.
   */
  @Input() preciosEval: PreciosEvaluacion | null = null;

  readonly num = num;
  readonly fechaDmy = fechaDmy;
  readonly esHttps = esHttps;
  readonly idTitulo = `hfc-titulo-${++siguiente}`;

  /** Se arman una vez por ficha: solo lo que viene. */
  precios: Precio[] = [];
  listas: Lista[] = [];
  benchmarks: HomologacionFichaBenchmark[] = [];
  /** «El harness midió con … y el catálogo dice …», si difieren; vacío si no. */
  avisoPrecios = "";

  constructor(@Inject(LOCALE_ID) private locale: string) {}

  ngOnChanges(): void {
    const f = this.ficha;
    const p = (concepto: string, v: unknown): Precio[] => {
      const n = num(v);
      return n === null ? [] : [{ concepto, valor: n }];
    };
    this.precios = [
      ...p("Entrada", f?.precioEntrada),
      ...p("Salida", f?.precioSalida),
      ...p("Caché: escritura 5 min", f?.precioCacheEscritura5m),
      ...p("Caché: escritura 1 h", f?.precioCacheEscritura1h),
      ...p("Caché: lectura", f?.precioCacheLectura),
      ...p("Batch: entrada", f?.precioBatchEntrada),
      ...p("Batch: salida", f?.precioBatchSalida),
      ...p("Modo Fast: entrada", f?.precioFastEntrada),
      ...p("Modo Fast: salida", f?.precioFastSalida),
    ];
    const l = (titulo: string, items?: string[] | null): Lista[] => {
      const limpios = Array.isArray(items) ? items.filter((x) => typeof x === "string" && !!x.trim()) : [];
      return limpios.length ? [{ titulo, items: limpios }] : [];
    };
    this.listas = [
      ...l("Disponibilidad", f?.accesos),
      ...l("Modalidades", f?.modalidades),
      ...l("Características", f?.caracteristicas),
      ...l("Casos de uso", f?.casosUso),
      ...l("Fortalezas", f?.fortalezas),
      ...l("Limitaciones", f?.limitaciones),
    ];
    this.benchmarks = Array.isArray(f?.benchmarks) ? f.benchmarks.filter((b) => !!b && typeof b === "object") : [];
    this.avisoPrecios = this.comparaPrecios(f, this.preciosEval);
  }

  /** Más de 90 días desde «verificado»: se avisa, no bloquea. */
  get vieja(): boolean {
    const d = num(this.ficha?.diasVerificada);
    return d !== null && d > DIAS_AVISO;
  }

  /** «(hace 3 días)», «(hace 1 día)» u «(hoy)». Los días los cuenta el servidor. */
  get antiguedad(): string {
    const d = num(this.ficha?.diasVerificada);
    if (d === null) {
      return "";
    }
    return d <= 0 ? "(hoy)" : `(hace ${d} ${d === 1 ? "día" : "días"})`;
  }

  /**
   * Los precios con que midió el harness (precios_usd_mtok) contra los de la
   * ficha: los de la evaluación a la vista si los pasan; si no, los de la
   * última evaluación no reemplazada del modelo, que manda el servidor. Los
   * dos lados guardan hasta 6 decimales: se comparan tal cual, sin redondear.
   * Solo se dice que difieren: el costo medido no cambia.
   */
  private comparaPrecios(f: HomologacionFicha | null | undefined, e: PreciosEvaluacion | null): string {
    if (!f) {
      return "";
    }
    const harness = e
      ? { entrada: e.entrada, salida: e.salida, cacheLectura: e.cacheLectura }
      : { entrada: f.preciosHarnessEntrada, salida: f.preciosHarnessSalida, cacheLectura: f.preciosHarnessCacheLectura };
    const pares = [
      { concepto: "entrada", harness: num(harness.entrada), catalogo: num(f.precioEntrada) },
      { concepto: "salida", harness: num(harness.salida), catalogo: num(f.precioSalida) },
      { concepto: "lectura de caché", harness: num(harness.cacheLectura), catalogo: num(f.precioCacheLectura) },
    ].filter((x) => x.harness !== null && x.catalogo !== null);
    if (!pares.some((x) => x.harness !== x.catalogo)) {
      return "";
    }
    const texto = (valor: (x: (typeof pares)[number]) => number | null) =>
      pares.map((x) => `${x.concepto} ${formatNumber(valor(x) as number, this.locale, FORMATO_PRECIO)}`).join(" · ");
    const moneda = f.moneda?.trim() || "sin moneda";
    const quien = e ? `La evaluación #${e.idEval}` : "La última evaluación no reemplazada del modelo";
    return `${quien} midió con ${texto((x) => x.harness)} USD por millón de tokens y el catálogo dice `
      + `${texto((x) => x.catalogo)} ${moneda}. La ficha no cambia el costo medido.`;
  }
}
